import { defineStore } from 'pinia'
import { restaurantSettingsService } from '@/services/api/restaurantSettingsService'
import { RestaurantSettings } from '@/models/RestaurantSettings'

export const useRestaurantStore = defineStore('restaurant', {
  state: () => ({
    settings: null,
    loading: false,
    error: null,
    initialized: false,
    logoDataUrl: null
  }),
  
  getters: {
    name: (state) => state.settings?.name,
    taxRate: (state) => state.settings?.taxRate || 19,
    serviceChargePercentage: (state) => state.settings?.serviceChargePercentage || 0,
    currency: (state) => state.settings?.currency || 'EUR',
    enableDynamicTranslations: (state) => state.settings?.enableDynamicTranslations || false,
    supportedLanguages: (state) => state.settings?.supportedLanguages || ['de', 'en'],
    defaultLanguage: (state) => state.settings?.defaultLanguage || 'de',
    getLogoDataUrl: (state) => {
      console.log('LogoDataUrl Getter aufgerufen:', state);
      if (!state.settings || !state.settings.logoImageUrl) {
        console.log('Keine logoImageUrl gefunden');
        return null;
      }
      return state.logoDataUrl || state.settings.logoImageUrl;
    },
    getPrimaryColor: (state) => state.settings?.primaryColor || '#E2553D',
    isOnlinePaymentsEnabled: (state) => state.settings?.onlinePaymentsEnabled || false,
    isCardPaymentOnSiteEnabled: (state) => state.settings?.cardPaymentOnSiteEnabled || false,
    isActive: (state) => state.settings?.isActive === true,
    getName: (state) => state.settings?.name || 'Restaurant',
  },
  
  actions: {
    async initialize() {
      console.log('Initialisiere Restaurant-Store...');
      try {
        await this.fetchRestaurantSettings();
        this.initialized = true;
      } catch (error) {
        console.error('Fehler beim Initialisieren des Stores:', error);
        this.initialized = false;
        throw error;
      }
    },

    async fetchRestaurantSettings() {
      try {
        // Verwende den restaurantSettingsService, der bereits die korrekte API-Instanz mit Auth-Headers verwendet
        const response = await restaurantSettingsService.getSettings();
        
        this.settings = response;
        
        if (this.settings && this.settings.logoImageUrl) {
          await this.loadLogo();
        }
        
        return this.settings;
      } catch (error) {
        console.error('Fehler beim Laden der Restaurant-Einstellungen:', error);
        throw error;
      }
    },
    
    async loadLogo() {
      if (!this.settings || !this.settings.logoImageUrl) return;

      try {
        if (this.settings.logoImageUrl.startsWith('data:')) {
          this.logoDataUrl = this.settings.logoImageUrl;
          return;
        }

        const { getImageUrl } = await import('@/config');
        this.logoDataUrl = getImageUrl(this.settings.logoImageUrl);
      } catch (error) {
        console.error('Fehler beim Laden des Logos:', error);
        this.logoDataUrl = null;
      }
    },
    
    async updateRestaurantSettings(settingsData) {
      this.loading = true;
      this.error = null;
      
      try {
        const updatedSettings = new RestaurantSettings({
          ...this.settings,
          ...settingsData
        });
        
        const data = await restaurantSettingsService.updateSettings(updatedSettings.toJSON());
        
        this.settings = new RestaurantSettings(data);
        
        console.log('Restaurant-Einstellungen aktualisiert:', this.settings);
        return this.settings;
      } catch (error) {
        console.error('Fehler beim Aktualisieren der Restaurant-Einstellungen:', error);
        this.error = 'Fehler beim Aktualisieren der Restaurant-Einstellungen';
        throw error;
      } finally {
        this.loading = false;
      }
    },

    async toggleDynamicTranslations(enabled) {
      return this.updateRestaurantSettings({
        enableDynamicTranslations: enabled
      });
    },

    async updateSupportedLanguages(languages) {
      return this.updateRestaurantSettings({
        supportedLanguages: languages
      });
    },
    
    async updateSettings(settingsData) {
      this.loading = true;
      this.error = null;
      
      try {
        const data = await restaurantSettingsService.updateSettings(settingsData);
        this.settings = data;
        
        if (settingsData.logoImageUrl && settingsData.logoImageUrl !== this.settings?.logoImageUrl) {
          await this.loadLogo();
        }
        
        return this.settings;
      } catch (error) {
        console.error('Fehler beim Aktualisieren der Restaurant-Einstellungen:', error);
        this.error = error.response?.data?.message || 'Einstellungen konnten nicht aktualisiert werden';
        throw error;
      } finally {
        this.loading = false;
      }
    },
    
    async uploadLogo(file) {
      this.loading = true;
      this.error = null;
      
      if (!file) {
        throw new Error('Keine Datei zum Hochladen ausgewählt');
      }

      const formData = new FormData();
      formData.append('LogoFile', file);
      
      try {
        console.log('Sende Logo-Upload-Request:', {
          fileName: file.name,
          fileType: file.type,
          fileSize: file.size
        });

        const response = await restaurantSettingsService.uploadLogo(formData);
        
        if (this.settings) {
          this.settings.logoImageUrl = response.data.logoImageUrl;
        }
        
        await this.loadLogo();
        
        return response.data;
      } catch (error) {
        console.error('Fehler beim Hochladen des Logos:', error);
        this.error = error.response?.data?.message || 'Logo konnte nicht hochgeladen werden';
        throw error;
      } finally {
        this.loading = false;
      }
    },

    removeLogoLocally() {
      if (this.settings) {
        this.settings.logoBase64 = null;
        this.settings.logoMimeType = null;
      }
    },
    
    setPrimaryColor(color) {
      if (this.settings) {
        this.settings.primaryColor = color;
      }
    },

    reset() {
      this.settings = new RestaurantSettings();
      this.loading = false;
      this.error = null;
    },

    async updatePaymentSettings(paymentData) {
      this.loading = true;
      this.error = null;
      try {
        const mapped = {
          onlinePaymentsEnabled: paymentData.enableOnlinePayments,
          cardPaymentOnSiteEnabled: paymentData.enableCardPaymentOnSite,
          stripePublishableKey: paymentData.stripeApiKey,
          stripeWebhookSecret: paymentData.stripeWebhookSecret,
        };
        const data = await restaurantSettingsService.updatePaymentSettings(mapped);
        return data;
      } catch (error) {
        console.error('Fehler beim Aktualisieren der Zahlungseinstellungen:', error);
        this.error = error.response?.data?.message || 'Zahlungseinstellungen konnten nicht aktualisiert werden';
        throw error;
      } finally {
        this.loading = false;
      }
    },

    async removeLogo() {
      this.loading = true;
      this.error = null;
      
      try {
        if (!this.settings) {
          throw new Error('Keine Einstellungen gefunden');
        }

        const updatedSettings = {
          ...this.settings,
          logoImageUrl: null
        };

        console.log('Entferne Logo mit Einstellungen:', updatedSettings);
        
        const data = await restaurantSettingsService.updateSettings(updatedSettings);
        
        if (data) {
          this.settings = {
            ...data,
            logoImageUrl: null
          };
          this.logoDataUrl = null;
        } else {
          throw new Error('Ungültige Antwort vom Server');
        }
      } catch (error) {
        console.error('Fehler beim Entfernen des Logos:', error);
        this.error = error;
        throw error;
      } finally {
        this.loading = false;
      }
    },
  }
}) 